/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import DragHandleIcon from "@mui/icons-material/DragHandle";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  Paper,
  Popover,
  Switch,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Map } from "ol";
import React, { useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DraggingStyle,
  Droppable,
  DropResult,
  NotDraggingStyle,
} from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { flattenMapLayers } from "~/utils/flattenMapLayers";
import { CustomMapEventTypes } from "../types/map";
import { LayerExtraSettings } from "./LayerSettings";
import { Layer } from "./MapDrawer";

const useStyles = makeStyles((theme) => ({
  list: {
    width: "100%",
  },
  dragIcon: {
    display: "flex",
    marginRight: theme.spacing(2),
  },
}));

interface Props {
  title: string;
  map: Map;
  extraSettings?: LayerExtraSettings[];
}

const getItemStyle = (
  isDragging: boolean,
  draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
) => ({
  // styles we need to apply on draggables
  ...draggableStyle,

  ...(isDragging && {
    background: "rgb(235,235,235,0.2)",
  }),
});

const DraggableSettingsList = ({
  title,
  map,
  extraSettings,
}: Props): JSX.Element => {
  const { t } = useTranslation("map");
  const classes = useStyles();
  const theme = useTheme();
  const [filteredLayers, setFilteredLayers] = useState<Layer[]>([]);
  const [update, setUpdate] = useState(false);
  const [clickedSettingsTitle, setClickedSettingsTitle] = React.useState<
    string | undefined
  >();

  const [extraSettingsAnchorEl, setExtraSettingsAnchorEl] =
    useState<HTMLButtonElement>();

  useEffect(() => {
    const handler = () => {
      setUpdate((prev) => !prev);
    };
    map.on(CustomMapEventTypes.SettingsChanged, handler);
    return () => {
      map.un(CustomMapEventTypes.SettingsChanged, handler);
    };
  }, [map]);

  useEffect(() => {
    const layers = flattenMapLayers(map);

    const filtered = layers.filter(
      (l) =>
        l.get("groupTitle") === title && l.get("hideSettings") === undefined,
    );
    if (!filtered?.length) return;
    const final = filtered.map((l) => {
      const newL: Layer = {
        group: l.get("groupTitle"),
        title: l.get("title"),
        visible: l.get("visible"),
        zIndex: l.getZIndex(),
      };
      return newL;
    });
    const sorted = final.sort((a, b) => b.zIndex - a.zIndex);
    setFilteredLayers(sorted);
  }, [title, map, update]);

  const handleDragEnd = (result: DropResult) => {
    // Reorder the layers if the drop destination was correct
    if (!result.destination) {
      return;
    }
    const layersCopy = [...filteredLayers];
    const [removed] = layersCopy.splice(result.source.index, 1);
    layersCopy.splice(result.destination?.index ?? 0, 0, removed);
    const final: Layer[] = [];

    const mapLayers = flattenMapLayers(map);
    for (let i = 0; i < layersCopy.length; i += 1) {
      const l = layersCopy[i];
      const zIndexGroup = Math.floor(l.zIndex / 100) * 100;
      const actualLayer = mapLayers.find((lay) => lay.get("title") === l.title);
      if (!actualLayer) continue;
      actualLayer?.setZIndex(zIndexGroup + layersCopy.length - i);
      final.push({
        group: actualLayer.get("groupTitle"),
        title: actualLayer.get("title"),
        visible: actualLayer.get("visible"),
        zIndex: actualLayer.getZIndex(),
      });
    }
    setFilteredLayers(final);
    map.dispatchEvent(CustomMapEventTypes.SettingsChanged);
  };

  const handleVisibilityToggle = (layer: Layer) => {
    const mapLayers = flattenMapLayers(map);
    const actualLayer = mapLayers.find(
      (lay) => lay.get("title") === layer.title,
    );
    actualLayer?.setVisible(!layer.visible);
    setFilteredLayers((old) =>
      old.map((o) =>
        o.title !== layer.title ? o : { ...o, visible: !layer.visible },
      ),
    );
    map.dispatchEvent(CustomMapEventTypes.SettingsChanged);
  };

  const hasExtraSettings = React.useCallback(
    (l: Layer) => !!extraSettings?.find((e) => e.title === l.title),
    [extraSettings],
  );

  const getExtraSettings = React.useCallback(
    (l: string): JSX.Element | null => {
      const settings = extraSettings?.find((e) => e.title === l);
      if (!settings) return null;
      return React.cloneElement(settings.content, { map });
    },
    [extraSettings, map],
  );
  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={title} direction="vertical">
        {(provided) => (
          <List
            ref={provided.innerRef}
            {...provided.droppableProps}
            classes={{ root: classes.list }}
          >
            <Popover
              open={!!extraSettingsAnchorEl}
              anchorEl={extraSettingsAnchorEl}
              onClose={() => {
                setClickedSettingsTitle(undefined);
                setExtraSettingsAnchorEl(undefined);
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{ style: { borderRadius: 2 } }}
            >
              <Paper
                style={{
                  padding: theme.spacing(1),
                }}
              >
                <Grid container direction="column">
                  {clickedSettingsTitle
                    ? getExtraSettings(clickedSettingsTitle)
                    : null}
                </Grid>
              </Paper>
            </Popover>

            {filteredLayers.map((l, index) => (
              <Draggable
                key={l.title}
                draggableId={l.title}
                index={index}
                isDragDisabled={filteredLayers.length === 1}
              >
                {(innerProvided, innerSnapshot) => (
                  <ListItem
                    key={l.title}
                    ref={innerProvided.innerRef}
                    {...innerProvided.draggableProps}
                    {...innerProvided.dragHandleProps}
                    style={getItemStyle(
                      innerSnapshot.isDragging,
                      innerProvided.draggableProps.style,
                    )}
                  >
                    {filteredLayers.length === 1 ? null : (
                      <div className={classes.dragIcon}>
                        <DragHandleIcon />
                      </div>
                    )}
                    <FormControlLabel
                      label={t(l.title)}
                      control={<Switch checked={l.visible} color="secondary" />}
                      onChange={() => handleVisibilityToggle(l)}
                    />
                    {hasExtraSettings(l) ? (
                      <>
                        <IconButton
                          onClick={(e) => {
                            setClickedSettingsTitle(l.title);
                            setExtraSettingsAnchorEl(e.currentTarget);
                          }}
                          style={{ color: "inherit", marginLeft: "auto" }}
                          size="large"
                        >
                          <SettingsIcon />
                        </IconButton>
                      </>
                    ) : null}
                  </ListItem>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DraggableSettingsList;
